import { RefObject, useContext, useEffect } from 'react'
import { ConferenceContext } from 'contexts/conference'
import { updateTimeout, useLayoutUpdate } from 'components/useLayoutUpdate'

const useVideoResize = (
    wrapperRef: RefObject<HTMLDivElement>,
    videoContainerRef: RefObject<VideoPlayerContainer>,
    sidebarOpen: boolean
): void => {
    const conference = useContext(ConferenceContext)

    const resizeVideo = (): void => {
        const wrapper = wrapperRef.current
        const videoContainer = videoContainerRef.current

        if (wrapper && videoContainer) {
            const size = {
                height: wrapper.clientHeight,
                width: wrapper.clientWidth
            }
            conference.setVideoSize(size, videoContainer)
        }
    }

    const hideVideo = (): void => {
        const wrapper = wrapperRef.current
        const videoContainer = videoContainerRef.current
        if (wrapper && videoContainer) {
            wrapper.style.visibility = 'hidden'
            videoContainer.style.opacity = '0'
        }
    }

    const {
        triggerLayoutUpdate,
        addLayoutUpdateStartListener,
        addLayoutUpdateEndListener,
        removeLayoutUpdateStartListener,
        removeLayoutUpdateEndListener
    } = useLayoutUpdate()
    useEffect(() => {
        if (wrapperRef.current) {
            const resObs = new ResizeObserver(triggerLayoutUpdate)
            resObs.observe(wrapperRef.current)

            let showVideoTimeout: NodeJS.Timeout
            const showVideo = (): void => {
                const wrapper = wrapperRef.current
                const videoContainer = videoContainerRef.current
                if (wrapper && videoContainer) {
                    resizeVideo()
                    showVideoTimeout = setTimeout(() => {
                        wrapper.style.visibility = 'visible'
                        videoContainer.style.opacity = '1'
                    }, updateTimeout)
                }
            }
            showVideo()

            addLayoutUpdateStartListener(() => {
                clearTimeout(showVideoTimeout)
                hideVideo()
            })

            addLayoutUpdateEndListener(showVideo)
            return () => {
                removeLayoutUpdateStartListener(hideVideo)
                removeLayoutUpdateEndListener(showVideo)
            }
        }
    }, [])
    useEffect(() => resizeVideo(), [videoContainerRef.current])
}

export { useVideoResize }
